/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable @next/next/no-img-element */
import React, {
  useState, useMemo, useRef,
} from 'react';
import Button from '$/components/base/Button';
import CalcRadish from '$/components/CalcRadish';
import IconFont from '$/components/IconFont';
import { useOfficialStore } from '$/store/useOfficialStore';
import { getIsOverseaByCountryCode } from '$/common/utils/countryCodeUtil';
import { Popover } from 'antd';
import { NoSSR } from '@meitu/catalyze-next';
import { useClickAway } from 'ahooks';
import DiscordBtn from '@/components/Header/DiscordBtn';
import ProductOperation from '@/components/ProductOperation';
import UserCard from '@/components/UserCard';
import type { OfficialMenuGroup, OverseaFeaturesSubItem, OverseaFeatureItem } from '@/apiSdk/api.type';
import classNames from 'classnames';
import useIsMobile from '@/hooks/useIsMobile';
import { useRouter } from 'next/router';
import { useOfficialHomeContext } from '@/components/overseaHome/OfficialHomeContext';
import logo from './logo.png';
import styles from './index.module.scss';

/** 埋点名称统一转小写，并将“产品”映射为历史值 product；页面标题仍直接展示原始 category。 */
function getMenuTrackName(category: string) {
  return category === '产品' ? 'product' : category.toLowerCase();
}

/** 子菜单点击携带所属接口分组，避免新增分类仍被记录为产品。 */
function mtStatTrack({
  category, level_2_id, level_2_name, level_3_id, level_3_name,
}: MtStatTrackProps) {
  window.mtstat?.track('roboneo_official_page_guide_click', {
    level_1_id: 0,
    level_1_name: getMenuTrackName(category),
    level_2_id,
    level_2_name,
    level_3_id,
    level_3_name,
  });
}

export type OverseaOfficialHeaderAppearance = 'transparent' | 'masked';

interface ServerHeaderProps {
  scrolled?: boolean;
  showAlert?: boolean;
  appearance?: OverseaOfficialHeaderAppearance;
  downloadContent?: React.JSX.Element;
  menus: OfficialMenuGroup[];
  onLoginClick?: () => void;
  isLoggedIn?: boolean;
}

/**
 * 国内外官网及共用此导航的落地页按接口分组渲染桌面端和移动端菜单。
 * 官网语言跟随地区，桌面端和移动端均不提供语言切换入口。
 * SSR 输出导航结构，水合后由本组件处理展开、收起和点击；ClientHeader 仅补充外围业务状态。
 */
export default function ServerHeader({
  showAlert,
  scrolled,
  appearance = 'transparent',
  downloadContent,
  menus,
  onLoginClick,
  isLoggedIn,
}: ServerHeaderProps) {
  const officialHome = useOfficialHomeContext();
  // 官网 SSR 与水合共用请求国家码；未接入官网上下文的页面继续沿用 Store 判断。
  const isOversea = useOfficialStore((state) => (officialHome
    ? getIsOverseaByCountryCode(officialHome.countryCode)
    : state.isOversea()));
  // eslint-disable-next-line ban/ban
  const router = useRouter();
  const isMobile = useIsMobile();
  // 桌面端按接口分类名记录当前展开分组，悬浮切换或点击导航外部时更新。
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  useClickAway(() => {
    setActiveCategory(null);
  }, navRef);

  const isForcedMaskedHeader = appearance === 'masked';
  const isMaskedHeader = isForcedMaskedHeader || scrolled;
  const headerStyle = useMemo(() => {
    const top = isMobile ? '54px' : '44px';
    const background = isForcedMaskedHeader
      ? '#0a0a0a'
      : 'var(--background-backgroundMaskSecondary, rgba(0, 0, 0, 0.50))';
    return {
      background: `${isMaskedHeader ? background : 'transparent'}`,
      backdropFilter: `${isMaskedHeader ? 'blur(25px)' : 'none'}`,
      top: `${showAlert ? top : '0'}`,
    };
  }, [isForcedMaskedHeader, isMaskedHeader, showAlert, isMobile]);

  /** 点击任意分组的子菜单链接后收起桌面端下拉菜单。 */
  const onSubMenuClick = () => {
    setActiveCategory(null);
  };

  const onMouseEnterLoginButton = () => {
    console.log('onMouseEnterLoginButton');
    router.prefetch('/home').then(() => {
      console.log('prefetch home page success');
    }).catch((error) => {
      console.error('prefetch home page error', error);
    });
  };

  return (
    <header
      className={`${styles.header} ${isMaskedHeader ? styles.scrolled : ''}`}
      style={headerStyle}
    >
      <div className={styles.gradientBlur} style={{ visibility: isMaskedHeader ? 'hidden' : 'visible' }}>
        <div />
        <div />
        <div />
        <div />
        <div />
        <div />
      </div>
      <div className={styles.headerContainer}>
        {/* Logo 区域 */}
        <div className={styles.logoSection}>
          <a href="/" className={styles.logoLink}>
            <img src={logo.src} alt="RoboNeo Logo" className={styles.logoImage} />
          </a>
        </div>

        {/* 桌面端导航：分类名称与子菜单来自接口，SSR 和客户端使用同一份分组数据。 */}

        <nav className={styles.navSection} ref={navRef}>
          {/* 接口分组按原顺序渲染；Pricing 沿用位于第一个分组之后的位置。 */}
          {menus.map((group, index) => (
            <React.Fragment key={group.category}>
              <div
                className={styles.navItem}
                onMouseEnter={() => setActiveCategory(group.category)}
                onMouseLeave={() => setActiveCategory(null)}
                onClick={() => {
                  window.mtstat?.track('roboneo_official_page_icon_click', {
                    icon_id: `tab_${getMenuTrackName(group.category)}`,
                  });
                }}
              >
                {group.category}
                {group.list.length > 0 && (
                  <ProductMenuBox
                    category={group.category}
                    showProductMenu={activeCategory === group.category}
                    menus={group.list}
                    onSubMenuClick={onSubMenuClick}
                  />
                )}
              </div>
              {index === 0 && <PricingNavLink />}
            </React.Fragment>
          ))}
          {menus.length === 0 && <PricingNavLink />}
        </nav>

        <div className={styles.rightSection}>
          <div className={styles.icon_menu}>
            {/* product operation */}
            <div className={styles.icon_menu_item}>
              <NoSSR>
                <ProductOperation />
              </NoSSR>
            </div>

            <div className={styles.line} />

            {/* 与首页保持一致：国内展示微信二维码，海外由 DiscordBtn 按国家展示社媒入口。 */}
            {isOversea ? <DiscordBtn countryCode={officialHome?.countryCode} /> : (
              <Popover
                placement="bottom"
                trigger={['hover']}
                overlayClassName="resetPopoverAndArrow"
                content={(
                  <div className={styles.wechatQrcodeContent}>
                    <img src="https://titan-img.meitudata.com/roboneo/image/home/wechat_qrcode.png" alt="" crossOrigin="anonymous" />
                  </div>
                )}
              >
                <div className={styles.icon_menu_item}>
                  <IconFont name="wechat" fontSize={16} />
                </div>
              </Popover>
            )}

            <div className={styles.line} />

            {/* mobile phone */}
            <Popover
              placement="bottom"
              trigger={['hover']}
              overlayClassName="resetPopoverAndArrow"
              content={downloadContent}
            >
              <div className={styles.icon_menu_item}>
                <IconFont name="mobilePhoneV2Bold" fontSize={16} />
              </div>
            </Popover>

          </div>
          {/* 复用用户卡片与萝卜入口，未登录时继续使用官网 SSR 文案。 */}
          <div className={styles.accountEntry}>
            {isLoggedIn ? (
              <UserCard options={{ hiddenSystemSetting: true }} />
            ) : (
              <Button
                className={styles.loginButton}
                type="plain"
                leftIcon={<IconFont name="userV3Bold" fontSize={16} />}
                onClick={onLoginClick}
                onMouseEnter={onMouseEnterLoginButton}
              >
                {officialHome?.messages.login ?? 'Login'}
              </Button>
            )}
          </div>
          <div className={styles.radishEntry}>
            <CalcRadish />
          </div>
        </div>

        {/* 移动端菜单按钮 */}
        <div className={styles.mobileMenuButton} onClick={() => setShowMobileMenu(!showMobileMenu)}>
          <IconFont name={showMobileMenu ? 'crossBold' : 'menuListV2Bold'} fontSize={16} color="var(--content-buttonSecondary)" />
        </div>
        <MobileNavSection showMobileMenu={showMobileMenu} menus={menus} onMaskClick={() => setShowMobileMenu(false)} />
      </div>

    </header>
  );
}

interface MtStatTrackProps {
  category: string;
  level_2_id: string;
  level_2_name: string;
  level_3_id: string;
  level_3_name: string;
}

/** 桌面端通用下拉菜单：左侧切换二级分类，右侧展示对应链接，适用于工具、文章等分组。 */
function ProductMenuBox({
  category, showProductMenu, menus, onSubMenuClick,
}: { category: string; showProductMenu: boolean; menus: OverseaFeatureItem[]; onSubMenuClick: () => void; }) {
  const [showInnerListIndex, setShowInnerListIndex] = useState(0);
  const height = useMemo(() => {
    const length = Math.max(menus.length, menus[showInnerListIndex]?.list?.length ?? 0);
    return length * 36 + 4 * (length - 1) + 40;
  }, [menus, showInnerListIndex]);

  const subMenus = useMemo(() => {
    return menus[showInnerListIndex]?.list ?? [];
  }, [menus, showInnerListIndex]);

  /** 记录链接所属的顶级分组、二级分类和条目，再收起下拉菜单。 */
  const openSubMenu = (subItem: OverseaFeaturesSubItem) => {
    mtStatTrack({
      category,
      level_2_id: `${menus[showInnerListIndex]?.category_id}`,
      level_2_name: menus[showInnerListIndex]?.category,
      level_3_id: `${subItem.id}`,
      level_3_name: subItem.title,
    });
    onSubMenuClick();
  };

  return (
    <div className={styles.outerProductMenuBox} style={{ display: showProductMenu ? 'block' : 'none' }}>
      <div
        className={styles.productMenuBox}
        style={{ height }}
      >
        <ul className={styles.productMenuList}>
          {menus.map((item: OverseaFeatureItem, index: number) => (
            <li className={styles.productMenuItem} key={item.category_id} onMouseEnter={() => setShowInnerListIndex(index)}>
              <span className={styles.productMenuItemLink}>{item.category}</span>
              <IconFont
                name="chevronRightFill"
                fontSize={10}
                color="var(--content-secondary)"
                className={styles.chevronRightIcon}
              />
            </li>
          ))}

        </ul>
        <ul className={styles.productSubMenuList}>
          {subMenus.map((subItem: OverseaFeaturesSubItem) => (
            <li className={styles.productSubMenuItem} key={subItem.id}>
              <a
                className={styles.productMenuItemLink}
                href={subItem.link}
                target="_blank"
                onClick={() => openSubMenu(subItem)}
              >
                {subItem.title}
              </a>
            </li>
          ))}
        </ul>

      </div>
    </div>
  );
}

/** Pricing 属于页面内锚点，独立于接口分组；移动端点击后同时关闭导航。 */
function PricingNavLink({ isMobile = false, onClick }: { isMobile?: boolean; onClick?: () => void; }) {
  return (
    <a
      href="#price"
      className={classNames(styles.navItem, { [styles.pricingNavItem]: !isMobile })}
      onClick={() => {
        window.mtstat?.track('roboneo_official_page_icon_click', { icon_id: 'tab_pricing' });
        onClick?.();
      }}
    >
      Pricing
      {isMobile && <IconFont name="chevronRightFill" fontSize={12} color="var(--content-secondary)" className={styles.chevronRightIcon} />}
    </a>
  );
}

/** 移动端同样遍历接口分组，任意时刻只展开一个分组，不提供语言切换入口。 */
function MobileNavSection({ showMobileMenu, menus, onMaskClick }: { showMobileMenu: boolean; menus: OfficialMenuGroup[]; onMaskClick: () => void; }) {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  return (
    <>
      <div className={styles.mobileNavMask} style={{ display: showMobileMenu ? 'block' : 'none' }} onClick={onMaskClick} />
      <nav className={classNames(styles.mobileNavSection, { [styles.show]: showMobileMenu })}>
        {menus.map((group, index) => (
          <React.Fragment key={group.category}>
            <div
              className={classNames(styles.navItem, { [styles.active]: activeCategory === group.category })}
              onClick={() => {
                window.mtstat?.track('roboneo_official_page_icon_click', {
                  icon_id: `tab_${getMenuTrackName(group.category)}`,
                });
                setActiveCategory(activeCategory === group.category ? null : group.category);
              }}
            >
              {group.category}
              <IconFont name="chevronRightFill" fontSize={12} color="var(--content-secondary)" className={styles.chevronRightIcon} />
            </div>
            {activeCategory === group.category && group.list.length > 0 && (
              <MobileProductMenu category={group.category} showProductMenu menus={group.list} />
            )}
            {index === 0 && <PricingNavLink isMobile onClick={onMaskClick} />}
          </React.Fragment>
        ))}
        {menus.length === 0 && <PricingNavLink isMobile onClick={onMaskClick} />}
      </nav>
    </>
  );
}

interface MobileProductMenuProps {
  category: string;
  showProductMenu: boolean;
  menus: OverseaFeatureItem[] | OverseaFeaturesSubItem[];
  indent?: number;
}

/** 移动端递归展示分组内的分类和链接，并将顶级分类名传递给条目点击埋点。 */
function MobileProductMenu({
  category, showProductMenu, menus, indent = 0,
}: MobileProductMenuProps) {
  const [showMenuIndex, setShowMenuIndex] = useState(-1);
  return (
    <ul
      className={styles.mobileProductMenuList}
      style={{
        display: showProductMenu ? 'block' : 'none',
        paddingLeft: indent * 24 + (indent === 0 ? 36 : 0),
      }}
    >
      {menus.map((item, index) => {
        const isLeaf = ((item as OverseaFeatureItem).list?.length ?? 0) === 0;
        const key = (item as OverseaFeatureItem).category_id ?? (item as OverseaFeaturesSubItem).id;
        const isActive = showMenuIndex === index;
        return (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <li
            className={styles.productMenuItem}
            key={key}
            onClick={(e) => {
              e.stopPropagation();
              if (isLeaf) {
                mtStatTrack({
                  category,
                  level_2_id: `${(item as OverseaFeatureItem).category_id}`,
                  level_2_name: (item as OverseaFeatureItem).category,
                  level_3_id: `${(item as OverseaFeaturesSubItem).id}`,
                  level_3_name: (item as OverseaFeaturesSubItem).title,
                });
              } else {
                setShowMenuIndex(isActive ? -1 : index);
              }
            }}
          >
            <div className={styles.inner_item_box}>
              {!isLeaf
                ? <span className={classNames(styles.productMenuItemLink, { [styles.hightlight]: isActive })}>{(item as OverseaFeatureItem).category || (item as OverseaFeaturesSubItem).title}</span>
                : <a className={styles.productMenuItemLink} href={(item as OverseaFeaturesSubItem).link}>{(item as OverseaFeatureItem).category || (item as OverseaFeaturesSubItem).title}</a>}
              {!isLeaf && <IconFont name={isActive ? 'chevronDownFill' : 'chevronRightFill'} fontSize={12} color="var(--content-secondary)" className={styles.chevronRightIcon} />}
            </div>
            {!isLeaf && (
            <MobileProductMenu
              category={category}
              showProductMenu={isActive}
              menus={(item as OverseaFeatureItem).list || []}
              indent={indent + 1}
            />
            )}
          </li>
        );
      })}
    </ul>
  );
}
